import { z } from "zod";
import { seatReservationSchema } from "./seat-reservation.js";
/**
 * `POST /internal/seats/reserve` on the game service — the one api -> game
 * internal call (architecture §2). Guarded by the `INTERNAL_SECRET` header.
 *
 * ── Renamed from `dev-join.ts` by §16f A60 ──────────────────────────────────
 *
 * This file used to lead with the Sprint 1 walking-skeleton join
 * (`POST /api/v1/dev/join`) and carry the internal reservation contract
 * underneath it. A60 deletes the dev surface outright — *"a capability you need
 * **nowhere** gets removed"* — so `devJoinRequestSchema` /
 * `devJoinResponseSchema` are gone and what remains is the one internal call,
 * under a name that says what it is. The join envelope those schemas described
 * lives on unchanged as `joinRoomResponseSchema` in `rooms.ts`; it was always
 * the same `{ reservation, redirected }` shape (§16 A11), which is why nothing
 * about the client's unwrap had to move.
 */
export const INTERNAL_SECRET_HEADER = "x-internal-secret";
export const reserveSeatRequestSchema = z.object({
    /**
     * Colyseus room name to reserve in — an `ENGINE_KEYS` member or
     * `LOBBY_ROOM_NAME`.
     *
     * **Required, with no default, since A60.** It used to default to `"ping"`,
     * which was the skeleton room; with `PingRoom` deleted a defaulted call would
     * matchmake into a room name nothing registers and fail with a Colyseus
     * internal error rather than a refusal. Both callers name their room
     * explicitly and always have.
     */
    roomName: z.string().min(1),
    /**
     * Reserve in **one named room**, by its Colyseus id, instead of matchmaking
     * by name (S4-4).
     *
     * A game room is never chosen by matchmaking. `joinOrCreate("null_game")`
     * would pick whichever room the driver returned first — a different
     * `room_configs` row from the one the player tapped — and, finding none
     * joinable, would **create** one with no `roomConfigId`, i.e. an orphan the
     * reaper sweeps in silence. The caller resolves the config's current
     * `filling` room from Postgres and names it here; the room's identity is a
     * database fact, not a matchmaking outcome.
     *
     * S4-5's `POST /api/v1/rooms/join` is this field's production caller. It is
     * added now because S4-4's phase machine cannot be exercised without a
     * deterministic way into a specific room, and inventing a second reservation
     * route for that would be the fork we would then have to reconcile.
     */
    roomId: z.string().min(1).optional(),
    /**
     * A diagnostic tag, logged and nothing else — the deploy verifier sets it to
     * tell its two clients apart in the log. It is **not** an identity: since
     * S2-4 the nickname comes from the `players` row loaded in `onAuth`, and no
     * room reads this.
     */
    label: z.string().trim().min(1).max(24).optional(),
    /**
     * **Whose hold this reservation is — bookkeeping, and NEVER an identity**
     * (T-219, Quinn B2).
     *
     * A Colyseus seat reservation is keyed by a `sessionId` the caller sees once,
     * in the response, and never again. So when the api's leave route tells the
     * room to let go of a seat that **no socket ever consumed**, the room has
     * nothing to match the player against and the reservation stands until
     * `seat_reservation_ms` expires — two of those lock a 1v1 room, which is how a
     * pair of *fully refunded* join+leave cycles denied an innocent funded player
     * the money tile for 20 seconds at zero cost. Tagging the hold here is what
     * lets `POST /internal/rooms/:id/seats/release` cancel the right one.
     *
     * ── The identity rule this does not bend (§7 / S2-4 DoD) ──────────────────
     *
     * Reservation options are **not** identity and this field must never be read
     * as one. Identity on a socket comes from the access token, verified in
     * `BaseGameRoom.onAuth`, and from the `players` row it loads — `onAuth` and
     * `onJoin` both ignore their `options` argument and will keep ignoring it. The
     * field is written only by `apps/api`'s join service over the internal
     * network (a client cannot reach `/internal/*`), it is read only by the
     * reservation-cancel path, and it authorises nothing: the money and the seat
     * are the `room_participants` row's, and the leave route has already proved
     * that row is the caller's before it asks the room for anything.
     */
    holdForPlayerId: z.string().uuid().optional(),
});
export const reserveSeatResponseSchema = z.object({
    reservation: seatReservationSchema,
    redirected: z.boolean(),
    /** Which Colyseus process served the matchmaking call (diagnostics only). */
    servedBy: z.object({
        processId: z.string(),
        port: z.number(),
        publicAddress: z.string(),
    }),
});
//# sourceMappingURL=internal-seats.js.map